import * as React from "react";
import { ausgelagertesFenster } from "../windowManager/browserFenster/fensterRegister";
import { OfficeOpenSurface } from "./OfficeOpenSurface";
import { useOfficeResume } from "./useOfficeResume";
import type { OfficeTarget } from "../../services/speEditingService";
import { useWindowManager } from "../windowManager/WindowManagerContext";
import { DocumentViewerContent } from "./DocumentViewerContent";
import { OfficeOpenDialog } from "./OfficeOpenDialog";
import { alleDokumente } from "../windowManager/browserFenster/fensterRegister";
import { istDom } from "../windowManager/browserFenster/domArt";

type OfficeDocument = { id: number; fileName: string; mimeType?: string | null };
type OpenMenu = (doc: OfficeDocument, anchor?: HTMLElement) => void;
const Context = React.createContext<OpenMenu | null>(null);
const missingProvider: OpenMenu = () => { throw new Error("OfficeOpenMenuProvider fehlt."); };

/** Gemeinsame Auswahl für Dokumentlisten, ohne WindowManager beim Dokumentklick. */
export function OfficeOpenMenuProvider({ children }: { children: React.ReactNode }) {
  const resumed = useOfficeResume();
  const { openWindow, setWindowTitle } = useWindowManager();
  const [selection, setSelection] = React.useState<{ doc: OfficeDocument; anchor?: HTMLElement; resumeTarget?: OfficeTarget; resumeInOwnTab?: boolean } | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const recentClick = React.useRef<{ element: HTMLElement; at: number } | null>(null);
  React.useEffect(() => {
    const remember = (event: MouseEvent) => {
      // Ein Klick auf einen SVG-Pfad gehört zur umgebenden Dokumentzeile.
      if (!(istDom(event.target, "Element"))) return;
      const element = event.target.closest<HTMLElement>("button, a, [role=button], [role=row]") || event.target;
      if (istDom(element, "HTMLElement")) recentClick.current = { element, at: Date.now() };
    };
    alleDokumente.addEventListener("click", remember, true);
    return () => alleDokumente.removeEventListener("click", remember, true);
  }, []);
  React.useEffect(() => {
    if (!resumed) return;
    if ("error" in resumed) setNotice(resumed.error);
    else setSelection({ doc: { id: resumed.intent.documentId, fileName: "" }, resumeTarget: resumed.intent.target, resumeInOwnTab: resumed.intent.ownTab === true });
  }, [resumed]);
  const open = React.useCallback<OpenMenu>((doc, anchor) => {
    const click = recentClick.current;
    const target = anchor || (click && Date.now() - click.at < 1000 ? click.element : undefined);
    setNotice(null);
    setSelection({ doc, anchor: target?.isConnected ? target : undefined });
  }, []);
  const details = () => {
    if (!selection) return;
    const { doc } = selection;
    const id = `document-viewer-${doc.id}`;
    setSelection(null);
    openWindow({ id, title: doc.fileName, initialMaximized: true, browserFensterNeben: ausgelagertesFenster(), render: () =>
      <DocumentViewerContent docId={doc.id} fileName={doc.fileName} mimeType={doc.mimeType} officeZielwahl={false} onRenamed={name => setWindowTitle(id, name)} />,
    });
  };
  return <Context.Provider value={open}>
    {children}
    {selection && <OfficeOpenDialog key={`${selection.doc.id}:${selection.resumeTarget || "choose"}`}
      presentation="popover" anchor={selection.anchor} fileName={selection.doc.fileName}
      documentId={selection.doc.id} resumeTarget={selection.resumeTarget} resumeInOwnTab={selection.resumeInOwnTab}
      onDismiss={() => setSelection(null)} onOpened={() => {}}
      onDetails={selection.resumeTarget ? undefined : details} />}
    {notice && <OfficeOpenSurface presentation="popover" title="Microsoft-Anmeldung" onDismiss={() => setNotice(null)}>
      <div className="office-open-feedback"><p role="alert">{notice}</p></div>
    </OfficeOpenSurface>}
  </Context.Provider>;
}

export function useOfficeOpenMenu(): OpenMenu { return React.useContext(Context) || missingProvider; }
